// Set by the header's Sign out and cleared by the next successful sign-in.
const SIGNED_OUT_KEY = 'sceneCreator_signedOut'

// library-v1's saved return path. A deep link has to replace it; the app-root
// landing after /logout must not (see prepareOpenedScene).
const RETURN_PATH_KEYS = [
  'auth_originalPathname',
  'auth_originalPathname_at',
  'auth_originalPathname_forced',
  'auth_originalPathname_account',
]

const sceneIdFromPath = (pathname) => {
  const match = /^\/(\d+)\/?$/.exec(pathname || '')
  return match ? match[1] : ''
}

const lastViewedSceneId = (storage) => {
  try {
    const scenes = JSON.parse(storage.getItem('lastViewedScenes'))
    return Array.isArray(scenes) && scenes.length ? String(scenes[0].id || '') : ''
  } catch {
    return ''
  }
}

// True when a stored ID token is missing its expiry or is already past it.
// A missing token is not "expired": there is nothing to drop.
const tokenExpired = (storage) => {
  try {
    const token = storage.getItem('auth_userToken')
    if (!token) return false
    const part = token.split('.')[1]
    if (!part) return true
    const payload = part.replace(/-/g, '+').replace(/_/g, '/')
    const padded = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=')
    const exp = JSON.parse(atob(padded)).exp
    return !(exp * 1000 > Date.now())
  } catch {
    return true
  }
}

const isLoginReturn = (hash) => new URLSearchParams(String(hash || '').replace(/^#/, '')).has('id_token')

/**
 * Studio opened a specific scene (VP-14688).
 *
 * An expired Scene Creator ID token still counts as a session, so the library
 * calls the API, gets a 401 and navigates to the login page's /logout. That
 * page revokes the shared refresh token, and the silent hand-off (VP-14220)
 * can no longer sign the user in. Dropping the expired token makes this a
 * normal token-free open: the login page still holds the Studio sign-in.
 *
 * `auth_originalPathname` left by an earlier forced logout is kept across this
 * visit, so the return opened that older scene (clicking /1243844 opened
 * /2127799). This visit is the scene the user just chose, so the saved path
 * goes. The app-root landing after /logout is not a scene path and is left
 * alone.
 *
 * @example
 * // location /1243844, localStorage.auth_userToken expired
 * prepareOpenedScene() // token and auth_originalPathname removed
 */
export function prepareOpenedScene(win = window) {
  try {
    if (!sceneIdFromPath(win.location.pathname)) return
    if (isLoginReturn(win.location.hash)) return
    const storage = win.localStorage
    if (tokenExpired(storage)) {
      storage.removeItem('auth_userToken')
      storage.removeItem('auth_userAccessToken')
      storage.removeItem('auth_userExpires_in')
    }
    RETURN_PATH_KEYS.forEach((key) => storage.removeItem(key))
  } catch {
    // storage or location unavailable: the library's own return path stands
  }
}

const withScene = (baseLoginUrl, sceneId) => {
  if (!sceneId) return baseLoginUrl
  try {
    const login = new URL(baseLoginUrl)
    const redirect = login.searchParams.get('redirect_uri')
    if (!redirect) return baseLoginUrl + sceneId
    const target = new URL(redirect)
    target.pathname = `/${sceneId}`
    login.searchParams.set('redirect_uri', target.toString())
    return login.toString()
  } catch {
    return baseLoginUrl + sceneId
  }
}

/**
 * The login URL. The scene in the address bar is the one the redirect must
 * reopen (VP-14688). `lastViewedScenes` is only the fallback for the app root
 * (VP-9970), and a sign-out the user chose drops that fallback (VP-14220).
 *
 * `lastViewedScenes` belongs to the browser, not the user. Without the
 * sign-out flag, once a root landing's saved path had expired, the next person
 * to sign in opened the previous user's scene. A forced logout doesn't set the
 * flag; the UI library brings that user back to their page itself.
 *
 * @example
 * // location /1243844, lastViewedScenes = [{id:'2127799'}]
 * sceneLoginUrl('https://auth/login?redirect_uri=https://sc/')
 * // '…redirect_uri=https://sc/1243844'
 *
 * @example
 * // location /, localStorage.lastViewedScenes = '[{"id":"abc"}]'
 * sceneLoginUrl('https://auth/login?redirect_uri=https://sc/') // '…redirect_uri=https://sc/abc'
 * markSignedOut()
 * sceneLoginUrl('https://auth/login?redirect_uri=https://sc/') // '…redirect_uri=https://sc/'
 */
export function sceneLoginUrl(baseLoginUrl, storage = window.localStorage, pathname = window.location.pathname) {
  const opened = sceneIdFromPath(pathname)
  if (opened) return withScene(baseLoginUrl, opened)
  try {
    if (storage.getItem(SIGNED_OUT_KEY)) return baseLoginUrl
  } catch {
    return baseLoginUrl
  }
  return withScene(baseLoginUrl, lastViewedSceneId(storage))
}

export function markSignedOut(storage = window.localStorage) {
  try {
    storage.setItem(SIGNED_OUT_KEY, '1')
  } catch {
    // storage unavailable: the login keeps the remembered scene
  }
}

export function clearSignedOut(storage = window.localStorage) {
  try {
    storage.removeItem(SIGNED_OUT_KEY)
  } catch {
    // nothing to clear
  }
}
